import React from 'react';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';
import { imageUrl } from '../lib/images';
import SparklePattern from '../components/SparklePattern';

export function Contact() {
  return (
    <>
      <SEO
        title="Kontakt | MA Hvidevareservice"
        description="Kontakt MA Hvidevareservice i Brønshøj. Ring på 69 30 64 45 eller send en mail til mahvidevareservice@gmail.com for hurtig service."
        ogImage="https://assets.auxodev.dk/assets/fa892c1f-aad0-492e-acb0-b4e96a2c8e1c/ma-standard.png"
      />

      {/* Header section */}
      <section className="relative bg-white border-b border-slate-200/80 py-12 sm:py-16 overflow-hidden isolate">
        <SparklePattern variant="blue" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#53b7ff] tracking-tight">
              Kontakt MA Hvidevareservice
            </h1>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              Ring direkte til teknikeren eller udfyld formularen nedenfor. Vi rykker hurtigt ud i Brønshøj og hele Storkøbenhavn.
            </p>
          </div>
        </div>
      </section>

      {/* Main contact section */}
      <section className="py-14 sm:py-20 bg-slate-50 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left Info Column */}
            <div className="lg:col-span-5 space-y-6">
              {/* Primary Contact Details Card without background, border or shadow */}
              <div className="space-y-6 py-2">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 mb-1">
                    MA Hvidevareservice
                  </h2>
                  <p className="text-xs text-slate-500 font-medium">
                    CVR: 46308409
                  </p>
                </div>

                <div className="space-y-4 text-sm text-slate-700">
                  <div className="flex items-start gap-3.5">
                    <div className="shrink-0 text-sky-600 pt-0.5">
                      <i className="fa-solid fa-location-dot text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Adresse</div>
                      <div className="font-medium text-slate-900 mt-0.5">
                        Ruten 75, 1. th.<br />
                        2700 Brønshøj
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="shrink-0 text-sky-600 pt-0.5">
                      <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Telefon (klikbart)</div>
                      <a
                        href="tel:69306445"
                        className="font-bold text-base text-slate-900 hover:text-sky-600 transition-colors block mt-0.5 whitespace-nowrap"
                      >
                        69 30 64 45
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="shrink-0 text-sky-600 pt-0.5">
                      <i className="fa-solid fa-envelope text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Email (klikbart)</div>
                      <a
                        href="mailto:mahvidevareservice@gmail.com"
                        className="font-semibold text-slate-900 hover:text-sky-600 transition-colors block mt-0.5 whitespace-nowrap break-all sm:break-normal"
                      >
                        mahvidevareservice@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="shrink-0 text-sky-600 pt-0.5">
                      <i className="fa-brands fa-facebook text-base" aria-hidden="true" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Facebook</div>
                      <a
                        href="https://www.facebook.com/people/MA-hvidevareservice/61563162135341/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-sky-600 hover:underline block mt-0.5"
                      >
                        Besøg Facebook-side
                      </a>
                    </div>
                  </div>
                </div>

                {/* Åbningstider fra scrape */}
                <div className="pt-4 space-y-3">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Åbningstider
                  </h3>
                  <div className="space-y-2 text-xs text-slate-700">
                    <div className="flex justify-between py-1">
                      <span className="text-slate-500">Man-tor:</span>
                      <span className="font-semibold">kl. 07.30-16.00</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-500">Fredag:</span>
                      <span className="font-semibold">kl. 08.00-14.30</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-500">Lørdag:</span>
                      <span className="font-semibold">Efter aftale</span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span className="text-slate-500">Søndag:</span>
                      <span className="font-semibold text-slate-400">Lukket</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service visit promise */}
              <div className="bg-slate-900 text-white rounded-xl p-6 space-y-2">
                <h4 className="text-base font-bold text-white">
                  Vi ringer altid 20 minutter før ankomst
                </h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  Så slipper du for at spilde hele dagen med at vente derhjemme.
                </p>
              </div>
            </div>

            {/* Right Form Column */}
            <div className="lg:col-span-7">
              <ContactForm
                title="Send en forespørgsel"
                subtitle="Fortæl os om maskinen og fejlen, så kontakter vi dig hurtigt med svar og overslag."
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Contact;
